import { KYC_VENDORS } from '../types'
import type { ScopeSectionSchema } from './types'

export const kycSections: ScopeSectionSchema[] = [
  {
    id: 'kyc_core_setup',
    tabId: 'kyc',
    formTitle: 'Core setup',
    formDescription: 'High-level decisions required to configure KYC verification',
    pdfTitle: 'KYC - Core setup',
    pdfDescription: 'High-level decisions required to configure KYC verification',
    highLevelSummary: 'KYC core setup decisions.',
    groups: [
      {
        id: 'kyc_tab_header',
        tabId: 'kyc',
        sectionId: 'kyc_core_setup',
        formTitle: 'KYC Configuration',
        formDescription: 'Identity verification settings and document types',
        pdfTitle: 'KYC Configuration',
        pdfDescription: 'Identity verification settings and document types',
      },
      {
        id: 'kyc_reporting_entity_group',
        tabId: 'advanced',
        sectionId: 'kyc_core_setup',
        formTitle: 'Compliance status',
        formDescription: 'Regulatory status that impacts permitted KYC data sources.',
        pdfTitle: 'Compliance status',
        pdfDescription: 'Regulatory status that impacts permitted KYC data sources.',
      },
      {
        id: 'kyc_vendor_group',
        tabId: 'advanced',
        sectionId: 'kyc_core_setup',
        formTitle: 'KYC Vendor',
        formDescription: 'Select the primary KYC vendor and provide details if needed.',
        helperText: 'Required for completion.',
        pdfTitle: 'KYC Vendor',
        pdfDescription: 'KYC vendor selection.',
      },
    ],
    fields: [
      {
        id: 'kyc_required',
        tabId: 'kyc',
        sectionId: 'kyc_core_setup',
        groupId: 'kyc_tab_header',
        path: 'kycConfig.kycRequired',
        valueType: 'tri_state',
        formLabel: 'KYC Verification Required',
        formDescription: 'Does this customer require KYC identity verification?',
        tooltip: 'Select whether KYC verification is required for this customer. Selecting "No" will hide all other KYC configuration fields.',
        pdfLabel: 'KYC Required',
        pdfDescription: 'Whether KYC verification is required for this customer.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_is_reporting_entity',
        tabId: 'advanced',
        sectionId: 'kyc_core_setup',
        groupId: 'kyc_reporting_entity_group',
        path: 'kycConfig.isReportingEntity',
        valueType: 'tri_state',
        formLabel: 'Is this customer a reporting entity under AML/CTF regulations?',
        formDescription: 'Choose Yes/No/Don\'t know.',
        tooltip: 'A reporting entity is subject to AML/CTF obligations. This affects whether credit header checks may be used.',
        pdfLabel: 'Reporting entity (AML/CTF)',
        pdfDescription:
          'Confirm whether the customer is a reporting entity (this impacts allowed data sources such as credit header).',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_vendor',
        tabId: 'advanced',
        sectionId: 'kyc_core_setup',
        groupId: 'kyc_vendor_group',
        path: 'kycConfig.vendor',
        valueType: 'select',
        formLabel: 'Primary KYC Vendor',
        formDescription: 'Select the primary provider for KYC verification.',
        tooltip: 'Choose the primary KYC provider used for identity verification.',
        pdfLabel: 'KYC Vendor',
        pdfDescription: 'Select the primary KYC vendor (or choose Other and specify).',
        options: KYC_VENDORS.map((v) => ({ value: v.id, label: v.label })),
        contributesToCompletion: true,
      },
      {
        id: 'kyc_other_vendor',
        tabId: 'advanced',
        sectionId: 'kyc_core_setup',
        groupId: 'kyc_vendor_group',
        path: 'kycConfig.otherVendor',
        valueType: 'text',
        formLabel: 'Other Vendor Name',
        formDescription: 'If “Other” is selected above, specify the vendor name.',
        placeholder: 'Enter vendor name',
        tooltip: 'Only required when vendor is set to Other.',
        pdfLabel: 'Other KYC Vendor',
        pdfDescription: 'Provide the vendor name.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'kycConfig.vendor', equals: 'other' }],
      },
    ],
  },
  {
    id: 'kyc_documents_regions',
    tabId: 'kyc',
    formTitle: 'Documents & regions',
    formDescription: 'Document types and customer geography to shape data sources and coverage',
    pdfTitle: 'KYC - Documents & regions',
    pdfDescription: 'Document types and customer geography to shape data sources and coverage',
    highLevelSummary: 'KYC document types and geography decisions.',
    groups: [
      {
        id: 'kyc_au_document_types',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'Australian Customers - Document Types',
        formDescription: 'Document types used for Australian customer verification.',
        tooltip: 'Australian document types selection.',
        helperText: 'Required for completion when Australia is a primary jurisdiction. Select at least one.',
        pdfTitle: 'AU document types',
        pdfDescription: 'Australian customer document types.',
      },
      {
        id: 'kyc_other_au_id_types_toggle_group',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'Other Australian ID types',
        pdfTitle: 'Other Australian ID types',
        pdfDescription: 'Other Australian ID types toggle.',
      },
      {
        id: 'kyc_other_au_document_types_text_group',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'Other Australian document types',
        pdfTitle: 'Other Australian document types',
        pdfDescription: 'Other Australian document types details.',
      },
      {
        id: 'kyc_additional_au_id_documents_group',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'Additional Australian ID documents',
        formDescription: 'Optional additional Australian identity documents.',
        tooltip: 'Additional Australian ID documents guidance.',
        pdfTitle: 'Additional AU ID documents',
        pdfDescription: 'Additional Australian ID documents.',
      },
      {
        id: 'kyc_credit_header_check',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'Enable Credit Header Check',
        formDescription: 'Additional verification via credit bureau data',
        tooltip: 'Credit bureau sources selection (and acting on behalf mode).',
        pdfTitle: 'Credit header check',
        pdfDescription: 'Whether credit header checks are enabled.',
      },
      {
        id: 'kyc_nz_document_types',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'NZ Document Types',
        formDescription: 'Document types used for New Zealand customer verification.',
        tooltip: 'NZ document types selection.',
        pdfTitle: 'NZ document types',
        pdfDescription: 'New Zealand customer document types.',
        visibleWhen: [{ dependsOn: 'kycConfig.acceptNzCustomers', equals: 'yes' }],
      },
      {
        id: 'kyc_international_selected_countries_group',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'Selected countries',
        pdfTitle: 'Selected countries',
        pdfDescription: 'Selected international KYC countries.',
      },
      {
        id: 'kyc_international_accepted_id_types_group',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'International Customers - Accepted ID Types',
        pdfTitle: 'International accepted ID types',
        pdfDescription: 'Accepted ID types by international country.',
      },
      {
        id: 'kyc_international_search_group',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        formTitle: 'Search and add other countries',
        pdfTitle: 'International country search',
        pdfDescription: 'Search and add other international countries.',
      },
    ],
    fields: [
      {
        id: 'kyc_document_types_au',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_au_document_types',
        path: 'kycConfig.documentTypesAu',
        valueType: 'multi_select',
        formLabel: 'Australian document types',
        formDescription: 'Document types used for Australian customer verification.',
        pdfLabel: 'Australian document types',
        pdfDescription: 'Select at least one Australian document type if Australia is a primary jurisdiction.',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_au_other_document_types_enabled',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_other_au_document_types_text_group',
        path: 'kycConfig.auOtherDocumentTypesEnabled',
        valueType: 'boolean',
        formLabel: 'Other (specify)',
        formDescription: 'Enable to specify additional Australian document types not listed above.',
        pdfLabel: 'Other AU document types enabled',
        pdfDescription: 'Whether other Australian document types are enabled.',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_au_other_document_types_text',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_other_au_document_types_text_group',
        path: 'kycConfig.auOtherDocumentTypesText',
        valueType: 'text',
        formLabel: 'Other Australian document types',
        formDescription: 'If enabled, specify additional Australian document types.',
        placeholder: 'e.g., Student ID, Government employee ID',
        pdfLabel: 'Other AU document types',
        pdfDescription: 'Other Australian document types details.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'kycConfig.auOtherDocumentTypesEnabled', equals: true }],
      },
      {
        id: 'kyc_enable_credit_header_check_mode',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_credit_header_check',
        path: 'kycConfig.enableConsentSources',
        valueType: 'select',
        formLabel: 'Enable Credit Header Check',
        formDescription: 'Additional verification via credit bureau data',
        tooltip: 'Credit bureau sources selection (and acting on behalf mode).',
        pdfLabel: 'Credit header check mode',
        pdfDescription: 'Whether credit header checks are enabled (requires reporting entity status).',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ],
      },
      {
        id: 'kyc_accept_nz_customers',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_nz_document_types',
        path: 'kycConfig.acceptNzCustomers',
        valueType: 'tri_state',
        formLabel: 'Accept New Zealand Customers',
        formDescription: 'Whether New Zealand customers should be supported.',
        tooltip: 'Enables NZ document types selection when set to Yes.',
        pdfLabel: 'Accept NZ customers',
        pdfDescription: 'Confirm whether New Zealand customers should be supported (enables NZ document settings).',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_document_types_nz',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_nz_document_types',
        path: 'kycConfig.documentTypesNz',
        valueType: 'multi_select',
        formLabel: 'NZ document types',
        formDescription: 'Document types used for New Zealand customer verification.',
        pdfLabel: 'NZ document types',
        pdfDescription: 'Select at least one NZ document type to support.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'kycConfig.acceptNzCustomers', equals: 'yes' }],
      },
      {
        id: 'kyc_foreign_passport_verification',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_international_accepted_id_types_group',
        path: 'kycConfig.dvsForeignPassport',
        valueType: 'select',
        formLabel: 'Foreign Passport Verification',
        formDescription: 'Select which verification method(s) should be used for foreign passports with Australian visas. DVS verifies identity and contributes to KYC pass/fail. VEVO verifies visa status and entitlements only (does not impact KYC results).',
        tooltip: 'Foreign passport verification options:\n\n• DVS (Document Verification Service): Verifies passport number, name, and date of birth against Department of Home Affairs visa records. Confirms visa is in effect. Contributes to passing KYC identity verification.\n\n• VEVO (Visa Entitlement Verification Online): Verifies current visa status, work rights, study limitations, visa expiry, and other entitlements. Does NOT impact KYC results - provides additional information only.\n\n• Both: Client requires both DVS for identity verification AND VEVO for visa entitlement checks.\n\nUse DVS for standard identity verification. Add VEVO when you need to confirm visa conditions (e.g., verifying work rights for employment).',
        pdfLabel: 'Foreign passport verification',
        pdfDescription: 'Verification method(s) for foreign passports: DVS (identity), VEVO (visa entitlements), or both.',
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'dvs', label: 'DVS Only (Identity Verification)' },
          { value: 'vevo', label: 'VEVO Only (Visa Status & Entitlements)' },
          { value: 'both', label: 'Both DVS and VEVO' },
          { value: 'na', label: 'Not Applicable' },
        ],
        contributesToCompletion: false,
      },
      {
        id: 'kyc_international_kyc_countries',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_international_selected_countries_group',
        path: 'kycConfig.internationalCountries',
        valueType: 'multi_select',
        formLabel: 'International KYC Countries',
        formDescription: 'Countries where international KYC verification is required.',
        tooltip: 'Select countries where you need additional international identity verification coverage.',
        pdfLabel: 'International KYC countries',
        pdfDescription: 'Countries where international KYC verification is required.',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_international_countries',
        tabId: 'kyc',
        sectionId: 'kyc_documents_regions',
        groupId: 'kyc_international_selected_countries_group',
        path: 'kycConfig.internationalCountries',
        valueType: 'multi_select',
        formLabel: 'International KYC Countries',
        formDescription: 'Countries where international KYC verification is required.',
        tooltip: 'Select countries where you need additional international identity verification coverage.',
        pdfLabel: 'International KYC countries',
        pdfDescription: 'Countries where international KYC verification is required.',
        contributesToCompletion: false,
      },
    ],
  },
  {
    id: 'kyc_compliance_data_requirements',
    tabId: 'advanced',
    formTitle: 'Compliance & data requirements',
    formDescription:
      'Define regulatory requirements, required data, matching tolerances, and pass/fail criteria',
    pdfTitle: 'KYC - Compliance & data requirements',
    pdfDescription:
      'Define regulatory requirements, required data, matching tolerances, and pass/fail criteria',
    highLevelSummary: 'KYC compliance requirements and required verification data.',
    groups: [
      {
        id: 'kyc_rulesets_group',
        tabId: 'kyc',
        sectionId: 'kyc_compliance_data_requirements',
        formTitle: 'KYC Rulesets',
        formDescription: 'Define one or more KYC rulesets for identity verification. Each ruleset specifies how many independent data sources must verify each attribute (address, date of birth, full name, government ID).',
        tooltip: 'KYC rulesets define verification requirements. A ruleset specifies the number of data sources (0-3) needed to verify each attribute. For example, "2+2" means 2 independent data sources verifying 2 attributes. Clients can have multiple rulesets for different verification scenarios or risk levels.',
        helperText: 'Required for completion. At least one ruleset must be configured.',
        pdfTitle: 'KYC Rulesets',
        pdfDescription: 'Configured KYC rulesets defining verification requirements.',
      },
      {
        id: 'kyc_required_data_elements_group',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        formTitle: 'Required Data Elements for Verification',
        formDescription: 'Select which data elements are required for verification.',
        tooltip: 'Required data elements used for verification and matching.',
        helperText: 'Required for completion. At least one data element must be set to Yes.',
        pdfTitle: 'Required data elements',
        pdfDescription: 'Required data elements for verification.',
      },
      {
        id: 'kyc_match_thresholds_group',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        formTitle: 'Match Thresholds & Tolerances',
        formDescription: 'Define match thresholds, tolerances, and pass/fail criteria.',
        tooltip: 'Match thresholds and tolerances guidance.',
        pdfTitle: 'Match thresholds & tolerances',
        pdfDescription: 'Match thresholds and tolerances.',
      },
    ],
    fields: [
      {
        id: 'kyc_compliance_framework',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.complianceFramework',
        valueType: 'select',
        formLabel: 'Compliance Framework',
        formDescription: 'Select the compliance framework or regulatory standard to implement.',
        tooltip: 'This helps decide minimum data sources and verification rules.',
        pdfLabel: 'Compliance framework',
        pdfDescription:
          'Select the compliance level (e.g. Safe Harbour 2+2 vs Standard 1+1) to guide ruleset/workflow requirements.',
        options: [
          { value: 'unknown', label: 'Unknown / To Be Determined' },
          { value: 'safe_harbour_2x2', label: 'AML/CTF Safe Harbour (2+2)' },
          { value: 'standard_1x1', label: 'AML/CTF Standard (1+1)' },
          { value: 'custom', label: 'Custom regulatory requirement' },
          { value: 'none', label: 'No specific regulatory requirement' },
        ],
        contributesToCompletion: false,
      },
      {
        id: 'kyc_minimum_data_sources',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.minimumDataSources',
        valueType: 'select',
        formLabel: 'Minimum data sources',
        formDescription: 'Select the minimum number of independent data sources required to pass.',
        pdfLabel: 'Minimum data sources',
        pdfDescription: 'Select the minimum number of independent data sources required to pass.',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: '1', label: '1' },
          { value: '2', label: '2' },
          { value: 'custom', label: 'Custom' },
        ],
      },
      {
        id: 'kyc_enable_cascade',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.enableCascade',
        valueType: 'select',
        formLabel: 'Enable cascade/failover',
        formDescription: 'Confirm whether the system should try fallback data sources when the first source fails to match.',
        pdfLabel: 'Enable cascade/failover',
        pdfDescription: 'Confirm whether the system should try fallback data sources when the first source fails to match.',
        contributesToCompletion: false,
        options: [
          { value: 'tbd', label: "Don't know" },
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'kyc_rulesets',
        tabId: 'kyc',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_rulesets_group',
        path: 'kycConfig.rulesets',
        valueType: 'json',
        formLabel: 'KYC Rulesets',
        formDescription: 'Configure one or more KYC rulesets for identity verification. Each ruleset defines how many independent data sources (0-3) must verify each attribute.',
        tooltip: 'KYC rulesets define verification requirements:\n\n• One Plus (1+1): At least one independent data source confirms two attributes (e.g. Name + Address or Name + DOB). Low-risk, international baseline.\n\n• Two Plus (2+2): At least two independent data sources each confirm one or more attributes (e.g. one confirms Name, one confirms DOB). Complies with two-plus requirements.\n\n• GovID: Requires a valid government-issued document identifier (e.g. DVS document match, Passport, Driver Licence).\n\nYou can select from predefined rulesets for Australian, New Zealand, or International customers, or build custom rulesets specifying exact requirements.',
        pdfLabel: 'KYC Rulesets',
        pdfDescription: 'Configured KYC rulesets with verification requirements for each attribute',
        contributesToCompletion: true,
        visibleWhen: [{ dependsOn: 'kycConfig.kycRequired', notEquals: 'no' }],
      },
      {
        id: 'kyc_required_data_elements_summary',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_required_data_elements_group',
        path: 'kycConfig.requiredDataElements',
        valueType: 'text',
        formLabel: 'Required data elements (summary)',
        pdfLabel: 'Required data elements',
        pdfDescription: 'Confirm which data elements must be verified to pass (at least one should usually be required).',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_specific_regulatory_requirements',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.specificRegulatoryRequirements',
        valueType: 'textarea',
        formLabel: 'Specific Regulatory Requirements',
        formDescription: 'Capture any customer-specific regulatory obligations or constraints.',
        tooltip: 'Use this to document specific obligations (e.g., AUSTRAC, FINTRAC, etc.).',
        placeholder: 'e.g., Must comply with AUSTRAC reporting obligations, FINTRAC requirements, etc.',
        pdfLabel: 'Specific regulatory requirements',
        pdfDescription: 'Customer-specific regulatory requirements.',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_required_data_full_name',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_required_data_elements_group',
        path: 'kycConfig.requiredDataElements.fullName',
        valueType: 'tri_state',
        formLabel: 'Full Name (First + Last)',
        formDescription: 'Whether the user’s full name is required for verification.',
        tooltip: 'Typically required for identity verification and matching.',
        pdfLabel: 'Required data: Full name',
        pdfDescription: 'Whether full name is required for verification.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_required_data_middle_name',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_required_data_elements_group',
        path: 'kycConfig.requiredDataElements.middleName',
        valueType: 'tri_state',
        formLabel: 'Middle Name Required',
        formDescription: 'Whether a middle name is required for verification.',
        tooltip: 'Some sources may omit middle names; consider matching tolerance impact.',
        pdfLabel: 'Required data: Middle name',
        pdfDescription: 'Whether middle name is required for verification.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_required_data_date_of_birth',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_required_data_elements_group',
        path: 'kycConfig.requiredDataElements.dateOfBirth',
        valueType: 'tri_state',
        formLabel: 'Date of Birth',
        formDescription: 'Whether date of birth is required for verification.',
        tooltip: 'Often required for stronger matching and reduced false positives.',
        pdfLabel: 'Required data: Date of birth',
        pdfDescription: 'Whether date of birth is required for verification.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_required_data_address',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_required_data_elements_group',
        path: 'kycConfig.requiredDataElements.address',
        valueType: 'tri_state',
        formLabel: 'Address',
        formDescription: 'Whether address is required for verification.',
        tooltip: 'Used for matching and may determine availability of certain data sources.',
        pdfLabel: 'Required data: Address',
        pdfDescription: 'Whether address is required for verification.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_required_data_government_id',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_required_data_elements_group',
        path: 'kycConfig.requiredDataElements.governmentId',
        valueType: 'tri_state',
        formLabel: 'Government ID',
        formDescription: 'Whether a government ID number is required for verification.',
        tooltip: 'May enable stronger verification where document number checks are supported.',
        pdfLabel: 'Required data: Government ID',
        pdfDescription: 'Whether government ID is required for verification.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_name_matching_tolerance',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.nameMatchingTolerance',
        valueType: 'select',
        formLabel: 'Name Matching Tolerance',
        formDescription: 'Tolerance applied when matching names across sources.',
        tooltip: 'Lower tolerance increases pass rates but may increase risk of false positives.',
        pdfLabel: 'Name matching tolerance',
        pdfDescription: 'Tolerance applied when matching names across sources.',
        options: [
          { value: 'tbd', label: 'To Be Determined' },
          { value: 'exact_100', label: 'Exact Match (100%)' },
          { value: 'very_high_95', label: 'Very High (95%)' },
          { value: 'high_90', label: 'High (90%)' },
          { value: 'standard_85', label: 'Standard (85%)' },
          { value: 'medium_70_80', label: 'Medium (70-80%)' },
        ],
        contributesToCompletion: false,
      },
      {
        id: 'kyc_dob_matching_tolerance',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.dobMatchingTolerance',
        valueType: 'select',
        formLabel: 'Date of Birth Tolerance',
        formDescription: 'Tolerance applied when matching date of birth across sources.',
        tooltip: 'Defines whether minor variations are allowed.',
        pdfLabel: 'Date of birth tolerance',
        pdfDescription: 'Tolerance applied when matching date of birth across sources.',
        options: [
          { value: 'tbd', label: 'To Be Determined' },
          { value: 'exact', label: 'Exact Match Required' },
          { value: 'minor_variations', label: 'Allow Minor Variations' },
        ],
        contributesToCompletion: false,
      },
      {
        id: 'kyc_address_matching_tolerance',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.addressMatchingTolerance',
        valueType: 'select',
        formLabel: 'Address Matching Tolerance',
        formDescription: 'Tolerance applied when matching addresses across sources.',
        tooltip: 'Defines how strictly addresses must match to pass.',
        pdfLabel: 'Address matching tolerance',
        pdfDescription: 'Tolerance applied when matching addresses across sources.',
        options: [
          { value: 'tbd', label: 'To Be Determined' },
          { value: 'exact_100', label: 'Exact Match (100%)' },
          { value: 'very_high_95', label: 'Very High (95%)' },
          { value: 'high_90', label: 'High (90%)' },
          { value: 'standard_85', label: 'Standard (85%)' },
          { value: 'partial', label: 'Partial Match Allowed' },
        ],
        contributesToCompletion: false,
      },
      {
        id: 'kyc_pass_fail_criteria',
        tabId: 'advanced',
        sectionId: 'kyc_compliance_data_requirements',
        groupId: 'kyc_match_thresholds_group',
        path: 'kycConfig.passFailCriteria',
        valueType: 'textarea',
        formLabel: 'Pass/Fail Criteria Definition',
        formDescription: 'Define the criteria used to determine pass/fail for verification.',
        tooltip: 'Document thresholds and logic required for a pass.',
        placeholder: 'e.g., All data elements must match at 85% or higher across minimum 2 sources',
        pdfLabel: 'Pass/fail criteria',
        pdfDescription: 'Criteria used to determine pass/fail for verification.',
        contributesToCompletion: false,
      },
    ],
  },
  {
    id: 'kyc_jurisdictions_sources',
    tabId: 'kyc',
    formTitle: 'Jurisdictions & data sources',
    formDescription: 'Customer jurisdictions and selected data sources to support verification.',
    pdfTitle: 'KYC - Jurisdictions & data sources',
    pdfDescription: 'Customer jurisdictions and selected data sources to support verification.',
    highLevelSummary: 'KYC jurisdictions and data source selection.',
    groups: [
      {
        id: 'kyc_primary_jurisdictions_group',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        formTitle: 'Primary Customer Jurisdictions',
        formDescription: 'Select primary jurisdictions for customers.',
        tooltip: 'Primary customer jurisdictions guidance.',
        helperText: 'Required for completion. Select at least one (or keep “Don\'t know”).',
        pdfTitle: 'Primary customer jurisdictions',
        pdfDescription: 'Primary customer jurisdictions.',
      },
      {
        id: 'kyc_credit_bureau_sources_group',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        formTitle: 'Credit Bureau Data Sources',
        formDescription: 'Select credit bureau sources used for verification.',
        tooltip: 'Credit bureau data sources guidance.',
        helperText:
          'Required for completion (when Reporting Entity = Yes). These can be No, but shouldn’t be left Unknown.',
        pdfTitle: 'Credit bureau data sources',
        pdfDescription: 'Credit bureau data sources.',
        visibleWhen: [{ dependsOn: 'kycConfig.isReportingEntity', equals: 'yes' }],
      },
      {
        id: 'kyc_international_data_sources_group',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        formTitle: 'International Data Sources',
        formDescription: 'Select international data sources used for verification.',
        tooltip: 'International data sources guidance.',
        helperText:
          'Required for completion (when international jurisdictions are selected). These can be No, but shouldn’t be left Unknown.',
        pdfTitle: 'International data sources',
        pdfDescription: 'International data sources.',
      },
    ],
    fields: [
      {
        id: 'kyc_au_government_sources_summary',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_primary_jurisdictions_group',
        path: 'kycConfig.auGovernmentSources',
        valueType: 'text',
        formLabel: 'AU government sources (summary)',
        pdfLabel: 'AU government sources',
        pdfDescription:
          'Confirm which AU government data sources are permitted/required for KYC (or mark N/A via No where relevant).',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_credit_bureau_sources_summary',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_credit_bureau_sources_group',
        path: 'kycConfig.creditBureauSources',
        valueType: 'text',
        formLabel: 'Credit bureau sources (summary)',
        pdfLabel: 'Credit bureau sources',
        pdfDescription: 'Confirm whether credit header data sources are allowed (often depends on Reporting Entity status).',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_primary_customer_jurisdictions',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_primary_jurisdictions_group',
        path: 'kycConfig.primaryCustomerJurisdictions',
        valueType: 'multi_select',
        formLabel: 'Primary Customer Jurisdictions',
        formDescription: 'Select the primary jurisdictions where your customers are located.',
        tooltip: 'This drives which data sources and document types should be configured.',
        pdfLabel: 'Primary customer jurisdictions',
        pdfDescription: 'Primary jurisdictions where customers are located.',
        contributesToCompletion: true,
        options: [
          { value: "Don't know", label: "Don't know" },
          { value: 'Australia', label: 'Australia' },
          { value: 'New Zealand', label: 'New Zealand' },
          { value: 'United States', label: 'United States' },
          { value: 'United Kingdom', label: 'United Kingdom' },
          { value: 'Canada', label: 'Canada' },
          { value: 'European Union Countries', label: 'European Union Countries' },
          { value: 'Asia-Pacific (specify in notes)', label: 'Asia-Pacific (specify in notes)' },
          { value: 'Latin America (specify in notes)', label: 'Latin America (specify in notes)' },
          { value: 'Middle East & Africa (specify in notes)', label: 'Middle East & Africa (specify in notes)' },
          { value: 'Other (specify in notes)', label: 'Other (specify in notes)' },
          { value: 'Unknown / To Be Determined', label: 'Unknown / To Be Determined' },
        ],
      },
      {
        id: 'kyc_geographic_coverage_notes',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_primary_jurisdictions_group',
        path: 'kycConfig.geographicCoverageNotes',
        valueType: 'textarea',
        formLabel: 'Geographic Coverage Notes',
        formDescription: 'Add notes about geographic coverage expectations or edge cases.',
        tooltip: 'Use this to capture nuance (e.g., % split across jurisdictions).',
        placeholder: 'e.g., Primarily Australian customers but expect 10% from NZ and UK',
        pdfLabel: 'Geographic coverage notes',
        pdfDescription: 'Notes about geographic coverage expectations.',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_credit_bureau_equifax_credit_header',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_credit_bureau_sources_group',
        path: 'kycConfig.creditBureauSources.equifaxCreditHeader',
        valueType: 'tri_state',
        formLabel: 'Equifax Credit Header',
        formDescription: 'Whether to use Equifax credit header data as a verification source.',
        tooltip:
          'Verifies name, DOB, and address against Equifax credit bureau headers. Standard fuzziness ~85% per element. Requires explicit consent.',
        pdfLabel: 'Credit bureau: Equifax credit header',
        pdfDescription: 'Equifax credit header source selection.',
        contributesToCompletion: true,
        visibleWhen: [{ dependsOn: 'kycConfig.isReportingEntity', equals: 'yes' }],
      },
      {
        id: 'kyc_credit_bureau_equifax_credit_header_via',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_credit_bureau_sources_group',
        path: 'kycConfig.creditBureauSources.equifaxCreditHeaderVia',
        valueType: 'select',
        options: [
          { value: 'unknown', label: "Don't Know" },
          { value: 'frankieone', label: 'Via FrankieOne' },
          { value: 'direct', label: 'Direct Connection' },
        ],
        formLabel: 'Equifax Connection',
        formDescription: 'How is this data source connected?',
        pdfLabel: 'Equifax Credit Header connection',
        pdfDescription: 'Connection method for Equifax.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'kycConfig.isReportingEntity', equals: 'yes' },
          { dependsOn: 'kycConfig.creditBureauSources.equifaxCreditHeader', equals: 'yes' },
        ],
      },
      {
        id: 'kyc_credit_bureau_equifax_credit_header_direct_note',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_credit_bureau_sources_group',
        path: 'kycConfig.creditBureauSources.equifaxCreditHeaderDirectNote',
        valueType: 'textarea',
        formLabel: 'Equifax Direct Connection Details',
        formDescription: 'Capture details about the direct connection, API credentials, special configuration, etc.',
        placeholder: 'e.g., Direct API credentials provided by client, specific endpoint configuration, rate limits, etc.',
        pdfLabel: 'Equifax direct connection details',
        pdfDescription: 'Details about Equifax direct connection.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'kycConfig.isReportingEntity', equals: 'yes' },
          { dependsOn: 'kycConfig.creditBureauSources.equifaxCreditHeader', equals: 'yes' },
          { dependsOn: 'kycConfig.creditBureauSources.equifaxCreditHeaderVia', equals: 'direct' },
        ],
      },
      {
        id: 'kyc_credit_bureau_experian_credit_header',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_credit_bureau_sources_group',
        path: 'kycConfig.creditBureauSources.experianCreditHeader',
        valueType: 'tri_state',
        formLabel: 'Experian Credit Header',
        formDescription: 'Whether to use Experian credit header data as a verification source.',
        tooltip: 'Verifies name, DOB, and address against Experian headers. Often used after Equifax.',
        pdfLabel: 'Credit bureau: Experian credit header',
        pdfDescription: 'Experian credit header source selection.',
        contributesToCompletion: true,
        visibleWhen: [{ dependsOn: 'kycConfig.isReportingEntity', equals: 'yes' }],
      },
      {
        id: 'kyc_credit_bureau_experian_credit_header_via',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_credit_bureau_sources_group',
        path: 'kycConfig.creditBureauSources.experianCreditHeaderVia',
        valueType: 'select',
        options: [
          { value: 'unknown', label: "Don't Know" },
          { value: 'frankieone', label: 'Via FrankieOne' },
          { value: 'direct', label: 'Direct Connection' },
        ],
        formLabel: 'Experian Connection',
        formDescription: 'How is this data source connected?',
        pdfLabel: 'Experian Credit Header connection',
        pdfDescription: 'Connection method for Experian.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'kycConfig.isReportingEntity', equals: 'yes' },
          { dependsOn: 'kycConfig.creditBureauSources.experianCreditHeader', equals: 'yes' },
        ],
      },
      {
        id: 'kyc_credit_bureau_experian_credit_header_direct_note',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_credit_bureau_sources_group',
        path: 'kycConfig.creditBureauSources.experianCreditHeaderDirectNote',
        valueType: 'textarea',
        formLabel: 'Experian Direct Connection Details',
        formDescription: 'Capture details about the direct connection, API credentials, special configuration, etc.',
        placeholder: 'e.g., Direct API credentials provided by client, specific endpoint configuration, rate limits, etc.',
        pdfLabel: 'Experian direct connection details',
        pdfDescription: 'Details about Experian direct connection.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'kycConfig.isReportingEntity', equals: 'yes' },
          { dependsOn: 'kycConfig.creditBureauSources.experianCreditHeader', equals: 'yes' },
          { dependsOn: 'kycConfig.creditBureauSources.experianCreditHeaderVia', equals: 'direct' },
        ],
      },
      {
        id: 'kyc_international_data_source_gdc',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_international_data_sources_group',
        path: 'kycConfig.internationalDataSources.gdc',
        valueType: 'tri_state',
        formLabel: 'GDC (Global Data Consortium) - 40+ countries',
        formDescription: 'Whether to use GDC as an international verification source.',
        tooltip: 'Coverage varies by country.',
        pdfLabel: 'International data source: GDC',
        pdfDescription: 'GDC international data source selection.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_international_data_source_centrix',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_international_data_sources_group',
        path: 'kycConfig.internationalDataSources.centrix',
        valueType: 'tri_state',
        formLabel: 'Centrix (New Zealand)',
        formDescription: 'Whether to use Centrix as an international/NZ verification source.',
        tooltip: 'NZ-specific verification (NZTA/DIA). Requires consent.',
        pdfLabel: 'International data source: Centrix',
        pdfDescription: 'Centrix data source selection.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_international_data_source_equifax_nz',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_international_data_sources_group',
        path: 'kycConfig.internationalDataSources.equifaxNz',
        valueType: 'tri_state',
        formLabel: 'Equifax NZ (New Zealand)',
        formDescription: 'Whether to use Equifax NZ as an international/NZ verification source.',
        tooltip: 'Alternative to Centrix.',
        pdfLabel: 'International data source: Equifax NZ',
        pdfDescription: 'Equifax NZ data source selection.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_international_data_source_asia_verify',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_international_data_sources_group',
        path: 'kycConfig.internationalDataSources.asiaVerify',
        valueType: 'tri_state',
        formLabel: 'AsiaVerify (China/Asia)',
        formDescription: 'Whether to use AsiaVerify as an international verification source.',
        tooltip: 'Used for Chinese individuals and some SEA countries.',
        pdfLabel: 'International data source: AsiaVerify',
        pdfDescription: 'AsiaVerify data source selection.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_international_data_source_monnai',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_international_data_sources_group',
        path: 'kycConfig.internationalDataSources.monnai',
        valueType: 'tri_state',
        formLabel: 'Monnai (India)',
        formDescription: 'Whether to use Monnai as an international verification source.',
        tooltip: 'Verifies Aadhar/PAN/passports depending on configuration.',
        pdfLabel: 'International data source: Monnai',
        pdfDescription: 'Monnai data source selection.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_international_data_source_other_regional_providers',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_international_data_sources_group',
        path: 'kycConfig.internationalDataSources.otherRegionalProviders',
        valueType: 'tri_state',
        formLabel: 'Other regional providers (specify in notes)',
        formDescription: 'Whether other regional providers are required.',
        tooltip: 'Use notes to describe.',
        pdfLabel: 'International data source: Other providers',
        pdfDescription: 'Other regional providers selection.',
        contributesToCompletion: true,
      },
      {
        id: 'kyc_data_source_notes',
        tabId: 'kyc',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_international_data_sources_group',
        path: 'kycConfig.dataSourceNotes',
        valueType: 'textarea',
        formLabel: 'Data Source Selection Notes',
        formDescription: 'Add notes about data source priorities or cascading behavior.',
        placeholder: 'e.g., Prefer DVS for government IDs, cascade to Equifax if DVS fails',
        pdfLabel: 'Data source notes',
        pdfDescription: 'Notes explaining data source selection strategy.',
        contributesToCompletion: false,
      },
    ],
  },
  {
    id: 'kyc_additional_configuration',
    tabId: 'advanced',
    formTitle: 'Additional configuration',
    formDescription: 'Additional requirements or special considerations for KYC.',
    pdfTitle: 'KYC - Additional configuration',
    pdfDescription: 'Additional requirements or special considerations for KYC.',
    highLevelSummary: 'Additional KYC requirements and notes.',
    groups: [
      {
        id: 'kyc_additional_configuration_group',
        tabId: 'advanced',
        sectionId: 'kyc_additional_configuration',
        formTitle: 'Additional Configuration',
        formDescription: 'Additional requirements or special considerations.',
        pdfTitle: 'Additional configuration',
        pdfDescription: 'Additional requirements or special considerations.',
      },
    ],
    fields: [
      {
        id: 'kyc_selected_data_sources',
        tabId: 'advanced',
        sectionId: 'kyc_jurisdictions_sources',
        groupId: 'kyc_data_sources_group',
        path: 'kycConfig.selectedDataSources',
        valueType: 'json',
        formLabel: 'Data Sources',
        formDescription: 'Data sources used for KYC verification across all workflows.',
        pdfLabel: 'Data sources',
        pdfDescription: 'Selected data sources and their connection methods (via FrankieOne or direct).',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_special_requirements',
        tabId: 'advanced',
        sectionId: 'kyc_additional_configuration',
        groupId: 'kyc_additional_configuration_group',
        path: 'kycConfig.specialRequirements',
        valueType: 'textarea',
        formLabel: 'Special Requirements or Considerations',
        formDescription: 'Additional requirements or special considerations for KYC.',
        placeholder:
          'e.g., VIP customers require enhanced verification; customers under 18 have different document requirements; require manual review for PEP/sanctions hits, partial matches below threshold, or repeated failed attempts',
        pdfLabel: 'Special requirements',
        pdfDescription: 'Special requirements or considerations for KYC.',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_aging_enabled',
        tabId: 'advanced',
        sectionId: 'kyc_additional_configuration',
        groupId: 'kyc_aging_group',
        path: 'kycConfig.agingEnabled',
        valueType: 'boolean',
        formLabel: 'Use Custom Aging Period',
        formDescription: 'Enable custom aging configuration for KYC data.',
        pdfLabel: 'Custom aging enabled',
        pdfDescription: 'Whether custom aging period is configured for KYC verification results.',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_aging_period_months',
        tabId: 'advanced',
        sectionId: 'kyc_additional_configuration',
        groupId: 'kyc_aging_group',
        path: 'kycConfig.agingPeriodMonths',
        valueType: 'number',
        formLabel: 'Aging Period (Months)',
        formDescription: 'Number of months before KYC results require re-verification.',
        pdfLabel: 'Aging period',
        pdfDescription:
          'KYC verification results older than this period (in months) will require re-verification. Default is 12 months (1 year). Results are automatically invalidated when key data changes (address, DOB, ID), or when name changes (treated as new person).',
        contributesToCompletion: false,
      },
      {
        id: 'kyc_aging_custom_config',
        tabId: 'advanced',
        sectionId: 'kyc_additional_configuration',
        groupId: 'kyc_aging_group',
        path: 'kycConfig.agingCustomConfig',
        valueType: 'textarea',
        formLabel: 'Additional Aging Configuration',
        formDescription: 'Custom aging rules or specific requirements for different data types.',
        placeholder: 'e.g., Different aging periods for DOB vs address data, special requirements for high-risk customers',
        pdfLabel: 'Custom aging configuration',
        pdfDescription: 'Special aging rules or requirements for specific data types (e.g., DOB, address).',
        contributesToCompletion: false,
      },
    ],
  },
]

export const getKycField = (fieldId: string) =>
  kycSections.flatMap((s) => s.fields).find((f) => f.id === fieldId)
